"use client";

import {
  CreatableCodeSelect,
  type CreatableCodeSelectMultiProps,
  type CreatableCodeSelectSingleProps,
} from "@/components/billing/creatable-code-select";
import {
  APISearchSelect,
  type APISearchSelectProps,
} from "@/components/ui/select/api-search-select";

import type { ICDTenCode } from "@/types/medical-codes";

import {
  useIcd10CodeOptionSearch,
  useIcd10Search,
} from "@/api/medical-codes-query";

/**
 * ICD-10 pickers that own their paged search, the way `LoadTemplateMenu` owns
 * `useEmailTemplatesInfinite`: each instance calls the hook itself, so two
 * pickers on one page (e.g. the receipt's diagnosis rows) never share a typed
 * term or a result list.
 */

/** `APISearchSelect` over ICD-10 codes, with infinite scroll. `excludeCodes`
 *  hides already-picked codes without ending paging early. */
export function Icd10SearchSelect({
  excludeCodes,
  ...props
}: Omit<APISearchSelectProps<ICDTenCode>, "onSearch" | "remote"> & {
  excludeCodes?: string[];
}) {
  const icd10Search = useIcd10Search({ excludeCodes });
  return <APISearchSelect<ICDTenCode> {...props} remote={icd10Search} />;
}

/** Creatable ICD-10 code chips (Super Bill, service defaults), with infinite
 *  scroll and the free-text "Use '<typed>'" escape. */
export function Icd10CodeSelect(
  props: Omit<CreatableCodeSelectMultiProps, "isMulti" | "onSearch" | "remote">,
) {
  const icd10Search = useIcd10CodeOptionSearch();
  return <CreatableCodeSelect isMulti {...props} remote={icd10Search} />;
}

/** The same creatable search, one pick at a time and emptied after each, for
 *  callers that list the picked codes themselves (scribe answers). Pass the
 *  listed codes as `excludeCodes`. */
export function Icd10CodePicker(
  props: Omit<
    CreatableCodeSelectSingleProps,
    "isMulti" | "onSearch" | "remote" | "value" | "clearAfterSelect"
  >,
) {
  const icd10Search = useIcd10CodeOptionSearch();
  return (
    <CreatableCodeSelect {...props} clearAfterSelect remote={icd10Search} />
  );
}
